/**
 * Auswertung des Trainings (F-34 C1) - rein und getestet. Grundlage ist
 * immer das Protokoll (tatsaechlich gemacht), nie der Plan; der Plan dient
 * nur als Soll fuer die Planerfuellung.
 */
import { addDays, differenceInDays, startOfWeek } from '@/lib/datetime';
import type { LocalDate } from '@/domain/types';
import { einheitenAm, planAm } from '@/domain/training/plan';
import { pace, protokollMinuten } from '@/domain/training/protokoll';
import type { EinheitTyp, PlanEinheit, TrainingsPlan, TrainingsProtokoll, Uebung } from '@/domain/training/typen';

const fertig = (p: TrainingsProtokoll) => p.status === 'fertig';

// --- Planerfuellung -----------------------------------------------------------

export interface GeplanteEinheit { datum: LocalDate; einheit: PlanEinheit }

export function geplanteEinheiten(plaene: TrainingsPlan[], einheiten: PlanEinheit[], von: LocalDate, bis: LocalDate): GeplanteEinheit[] {
  const out: GeplanteEinheit[] = [];
  for (let d = von; d <= bis; d = addDays(d, 1)) {
    const plan = planAm(plaene, d);
    if (plan) for (const e of einheitenAm(plan, einheiten, d)) out.push({ datum: d, einheit: e });
  }
  return out;
}

export interface Planerfuellung {
  geplant: number;
  erledigt: number;
  /** 0-1, `null` ohne geplante Einheiten. */
  anteil: number | null;
  ausgefallen: GeplanteEinheit[];
}

/**
 * Erledigt = am selben Tag ein fertiges Protokoll dieser geplanten Einheit.
 * Zukuenftige Tage (nach `heute`) zaehlen nicht als ausgefallen.
 */
export function planerfuellung(geplant: GeplanteEinheit[], protokolle: TrainingsProtokoll[], heute: LocalDate): Planerfuellung {
  const bisHeute = geplant.filter((g) => g.datum <= heute);
  const gemacht = new Set(protokolle.filter(fertig).filter((p) => p.planEinheitId).map((p) => `${p.datum}|${p.planEinheitId}`));
  const ausgefallen = bisHeute.filter((g) => !gemacht.has(`${g.datum}|${g.einheit.id}`) && g.datum < heute);
  const erledigt = bisHeute.filter((g) => gemacht.has(`${g.datum}|${g.einheit.id}`)).length;
  const zaehlbar = erledigt + ausgefallen.length;
  return { geplant: bisHeute.length, erledigt, anteil: zaehlbar ? erledigt / zaehlbar : null, ausgefallen };
}

// --- Umfang -------------------------------------------------------------------

export interface Umfang {
  minuten: number;
  km: number;
  einheiten: number;
  /** Arbeitssaetze (ohne Aufwaermen, ohne Mobility). */
  saetze: number;
  minutenJeTyp: Record<EinheitTyp, number>;
}

const leer = (): Record<EinheitTyp, number> => ({ kraft: 0, ausdauer: 0, zirkel: 0, mobility: 0, skill: 0 });

export function umfang(protokolle: TrainingsProtokoll[], von: LocalDate, bis: LocalDate): Umfang {
  const u: Umfang = { minuten: 0, km: 0, einheiten: 0, saetze: 0, minutenJeTyp: leer() };
  for (const p of protokolle) {
    if (!fertig(p) || p.datum < von || p.datum > bis) continue;
    const min = protokollMinuten(p);
    u.einheiten += 1;
    u.minuten += min;
    u.minutenJeTyp[p.typ] += min;
    // Nur Laeufe - die Kennzahl heisst „Laufen“, eine Radtour gehoert nicht hinein.
    if (istLauf(p)) u.km += p.ausdauer?.distanzKm ?? 0;
    if (p.typ !== 'mobility') u.saetze += p.uebungen.filter((x) => !x.uebersprungen).flatMap((x) => x.saetze).filter((s) => s.erledigt && s.art !== 'aufwaermen').length;
  }
  u.km = Math.round(u.km * 10) / 10;
  return u;
}

export interface Woche extends Umfang { montag: LocalDate }

/** Die letzten `n` Wochen bis einschliesslich der Woche von `bis`, aelteste zuerst. */
export function wochenreihe(protokolle: TrainingsProtokoll[], bis: LocalDate, n = 12): Woche[] {
  const letzte = startOfWeek(bis);
  return Array.from({ length: n }, (_, i) => {
    const montag = addDays(letzte, -7 * (n - 1 - i));
    return { montag, ...umfang(protokolle, montag, addDays(montag, 6)) };
  });
}

/** Anteile je Typ (nach Minuten) - fuer den Vergleich mit der gewuenschten Verteilung. */
export function verteilung(u: Umfang): Record<EinheitTyp, number> {
  const out = leer();
  if (!u.minuten) return out;
  for (const k of Object.keys(out) as EinheitTyp[]) out[k] = u.minutenJeTyp[k] / u.minuten;
  return out;
}

// --- Kraft --------------------------------------------------------------------

/**
 * Geschaetztes Maximalgewicht (1RM) nach Epley (1985): Gewicht × (1 + Wdh/30).
 * Bei einer Wiederholung das Gewicht selbst; ueber 12 Wiederholungen wird die
 * Schaetzung ungenau, sie zaehlt trotzdem (gekennzeichnet in der App).
 */
export function e1rm(gewicht: number, wdh: number): number {
  if (gewicht <= 0 || wdh <= 0) return 0;
  return wdh === 1 ? gewicht : Math.round(gewicht * (1 + wdh / 30) * 10) / 10;
}

export interface KraftPunkt {
  datum: LocalDate;
  /** Bestes geschaetztes 1RM der Einheit (0 bei reinen Koerpergewichtsuebungen). */
  e1rm: number;
  /** Schwerster Satz. */
  gewicht: number;
  /** Meiste Wiederholungen in einem Satz. */
  wdh: number;
  /** Gewicht × Wiederholungen ueber alle Arbeitssaetze. */
  volumen: number;
  /** Neuer Bestwert (1RM bzw. Wiederholungen ohne Gewicht). */
  bestwert: boolean;
}

export function kraftVerlauf(protokolle: TrainingsProtokoll[], uebungId: string): KraftPunkt[] {
  const punkte: KraftPunkt[] = [];
  let best = 0;
  let bestWdh = 0;
  const sortiert = protokolle.filter(fertig).sort((a, b) => a.datum.localeCompare(b.datum) || (a.ende ?? '').localeCompare(b.ende ?? ''));
  for (const p of sortiert) {
    const saetze = p.uebungen.filter((u) => u.uebungId === uebungId && !u.uebersprungen).flatMap((u) => u.saetze).filter((s) => s.erledigt && s.art !== 'aufwaermen');
    if (!saetze.length) continue;
    const e = Math.max(...saetze.map((s) => e1rm(s.gewicht ?? 0, s.wdh ?? 0)));
    const wdh = Math.max(...saetze.map((s) => s.wdh ?? 0));
    const punkt: KraftPunkt = {
      datum: p.datum, e1rm: e, gewicht: Math.max(...saetze.map((s) => s.gewicht ?? 0)), wdh,
      volumen: saetze.reduce((sum, s) => sum + (s.gewicht ?? 0) * (s.wdh ?? 0), 0),
      bestwert: e > 0 ? e > best : wdh > bestWdh,
    };
    best = Math.max(best, e);
    bestWdh = Math.max(bestWdh, wdh);
    punkte.push(punkt);
  }
  return punkte;
}

/** Uebungen mit Protokolleintraegen, haeufigste zuerst. */
export function trainierteUebungen(protokolle: TrainingsProtokoll[], bibliothek: ReadonlyMap<string, Uebung>): Uebung[] {
  const anzahl = new Map<string, number>();
  for (const p of protokolle.filter(fertig)) for (const u of p.uebungen) if (!u.uebersprungen && u.saetze.some((s) => s.erledigt)) anzahl.set(u.uebungId, (anzahl.get(u.uebungId) ?? 0) + 1);
  return [...anzahl].sort((a, b) => b[1] - a[1]).map(([id]) => bibliothek.get(id)).filter((u): u is Uebung => !!u && u.typ !== 'mobility');
}

// --- Laufen -------------------------------------------------------------------

export interface Lauf { datum: LocalDate; name: string; km: number; sek: number; pace: number }

/**
 * Ist das ein Lauf? Ausdrueckliche Sportart zaehlt; ohne Angabe gelten
 * geplante Ausdauereinheiten als Lauf, freie Aktivitaeten nur, wenn der Name
 * danach klingt (aeltere Eintraege) - eine Radtour verfaelscht sonst Tempo
 * und Laufziele.
 */
export function istLauf(p: Pick<TrainingsProtokoll, 'typ' | 'sportart' | 'frei' | 'name'>): boolean {
  if (p.typ !== 'ausdauer') return false;
  if (p.sportart) return p.sportart === 'laufen';
  return !p.frei || /lauf|jogg|run/i.test(p.name);
}

export function laeufe(protokolle: TrainingsProtokoll[]): Lauf[] {
  return protokolle.filter(fertig)
    .filter((p) => istLauf(p) && p.ausdauer?.distanzKm && p.ausdauer.dauerSek)
    .map((p) => ({ datum: p.datum, name: p.name, km: p.ausdauer!.distanzKm!, sek: p.ausdauer!.dauerSek!, pace: pace(p.ausdauer!.distanzKm, p.ausdauer!.dauerSek)! }))
    .sort((a, b) => a.datum.localeCompare(b.datum));
}

// --- Konsistenz und Belastung ------------------------------------------------

/** Aktive Wochen in Folge bis zur aktuellen (die laufende Woche zaehlt, wenn schon trainiert). */
export function serieAktiverWochen(protokolle: TrainingsProtokoll[], heute: LocalDate): number {
  const wochen = new Set(protokolle.filter(fertig).map((p) => startOfWeek(p.datum)));
  let w = startOfWeek(heute);
  if (!wochen.has(w)) w = addDays(w, -7);
  let n = 0;
  while (wochen.has(w)) { n++; w = addDays(w, -7); }
  return n;
}

/** Trainingsminuten je Tag - fuer die Heatmap. */
export function minutenJeTag(protokolle: TrainingsProtokoll[]): Map<LocalDate, number> {
  const m = new Map<LocalDate, number>();
  for (const p of protokolle.filter(fertig)) m.set(p.datum, (m.get(p.datum) ?? 0) + Math.max(1, protokollMinuten(p)));
  return m;
}

/**
 * Hinweis bei sprunghaft steigendem Umfang: aktuelle Woche im Verhaeltnis
 * zum Schnitt der vier Wochen davor (acute:chronic workload ratio). Ab 1,5
 * steigt laut Gabbett (2016, Br J Sports Med 50:273) das Verletzungsrisiko.
 * Nur ein Hinweis - keine Trainingsberatung.
 */
export const BELASTUNG_SCHWELLE = 1.5;

export function belastungsSprung(reihe: Woche[]): { verhaeltnis: number; aktuell: number; schnitt: number } | null {
  if (reihe.length < 5) return null;
  const aktuell = reihe[reihe.length - 1]!.minuten;
  const vorher = reihe.slice(-5, -1);
  const schnitt = vorher.reduce((s, w) => s + w.minuten, 0) / vorher.length;
  if (schnitt < 30 || aktuell <= schnitt * BELASTUNG_SCHWELLE) return null;
  return { verhaeltnis: Math.round((aktuell / schnitt) * 10) / 10, aktuell, schnitt: Math.round(schnitt) };
}

/** Ganze Wochen zwischen zwei Daten (fuer Trends). */
export const wochenZwischen = (a: LocalDate, b: LocalDate) => Math.floor(differenceInDays(startOfWeek(a), startOfWeek(b)) / 7);
